import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import apiClient from '../api/client';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../contexts/PermissionContext';
import { createCampaignRequests, type Campaign, type CampaignState } from './campaignRequests.mjs';

function WorkspaceCampaigns({ canManage }: { canManage: boolean }) {
  const [state, setState] = useState<CampaignState>({ kind: 'loading' });
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [page, setPage] = useState(0);
  const requests = useRef<ReturnType<typeof createCampaignRequests> | null>(null);
  useEffect(() => { const session = createCampaignRequests(apiClient, setState); requests.current = session; void session.load(); return () => session.dispose(); }, []);
  return <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
    <Typography variant="h6">WhatsApp campaigns</Typography>
    <Typography color="text.secondary" sx={{ mb: 2 }}>Review campaigns and edit draft details. Sending is not available from this panel.</Typography>
    {state.kind === 'loading' && <CircularProgress size={24} />}
    {state.kind === 'error' && <Alert severity="error" action={<Button onClick={() => void requests.current?.load(page)}>Retry</Button>}>Campaign request failed. Your draft input is preserved; retry to confirm the saved state.</Alert>}
    {editing && canManage && <Stack spacing={2} sx={{ my: 2 }}>
      <TextField label="Campaign name" value={editing.name} inputProps={{ maxLength: 200 }} onChange={e => setEditing({ ...editing, name: e.target.value })} />
      <TextField label="Description" multiline value={editing.description ?? ''} inputProps={{ maxLength: 2000 }} onChange={e => setEditing({ ...editing, description: e.target.value })} />
      <Button disabled={state.kind === 'loading' || !editing.name.trim()} onClick={() => { setPage(0); void requests.current?.save(editing.id, editing.name, editing.description ?? ''); }}>Save draft details</Button>
      <Button onClick={() => setEditing(null)}>Close editor</Button>
    </Stack>}
    {state.kind === 'ready' && <>
      {state.data.content.length === 0 && <Typography>No campaigns in this workspace.</Typography>}
      {state.data.content.map(campaign => <Box key={campaign.id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography fontWeight="bold">{campaign.name}</Typography>
        <Typography variant="body2">{campaign.status}</Typography>
        {canManage && campaign.status === 'DRAFT' && <Button onClick={() => setEditing(campaign)}>Edit details</Button>}
      </Box>)}
      <Button disabled={page === 0} onClick={() => { setPage(page - 1); void requests.current?.load(page - 1); }}>Previous</Button>
      <Button disabled={state.data.last} onClick={() => { setPage(page + 1); void requests.current?.load(page + 1); }}>Next</Button>
    </>}
  </Paper>;
}

export default function WhatsAppCampaigns() {
  const tenant = useTenant();
  const permissions = usePermissions();
  if (tenant.loading || permissions.loading || !tenant.organizationId || !tenant.workspaceId
      || !permissions.hasPermission('COMM_CAMPAIGN_VIEW')) return null;
  // Remount on a scope change: old drafts and in-flight responses cannot populate the new scope.
  return <WorkspaceCampaigns key={JSON.stringify([tenant.organizationId, tenant.workspaceId])}
    canManage={permissions.hasPermission('COMM_CAMPAIGN_MANAGE')} />;
}
